import { defineStore } from 'pinia'
import { useToastStore } from '~/stores/toast.store'

export const CART_STORAGE_KEY = 'cart'

export type CartKey = string | number

export interface CartItem {
    slug: CartKey
    count: number
}

export interface CartProductLimit {
    slug: CartKey
    max_cart_quantity: number | string | null
}

interface CartStoreState {
    cart: CartItem[]
    isHydrated: boolean
}

const getCartItem = (cart: CartItem[], slug: CartKey) =>
    cart.find((item) => String(item.slug) === String(slug))

const normalizeCartItem = (item: unknown): CartItem | null => {
    if (!item || typeof item !== 'object') {
        return null
    }

    const { slug, count } = item as Partial<CartItem>

    if (typeof slug !== 'string' && typeof slug !== 'number') {
        return null
    }

    const normalizedCount = Math.round(Number(count))

    if (!Number.isFinite(normalizedCount) || normalizedCount <= 0) {
        return null
    }

    return {
        slug,
        count: normalizedCount,
    }
}

export const normalizeCart = (payload: unknown): CartItem[] => {
    if (!Array.isArray(payload)) {
        return []
    }

    const normalizedCart = new Map<string, CartItem>()

    payload.forEach((item) => {
        const normalizedItem = normalizeCartItem(item)

        if (!normalizedItem) {
            return
        }

        const key = String(normalizedItem.slug)
        const existingItem = normalizedCart.get(key)

        if (existingItem) {
            existingItem.count += normalizedItem.count
            return
        }

        normalizedCart.set(key, normalizedItem)
    })

    return [...normalizedCart.values()]
}

export const useCartStore = defineStore('cartStore', {
    state: (): CartStoreState => ({
        cart: [],
        isHydrated: false,
    }),
    getters: {
        totalItems: (state) => {
            if (!state.isHydrated) {
                return 0
            }

            return state.cart.reduce((sum, item) => sum + item.count, 0)
        },
        isInCart: (state) => (slug: CartKey | null | undefined) => {
            if (!state.isHydrated || slug == null) {
                return false
            }

            return state.cart.some((item) => String(item.slug) === String(slug))
        },
        getItemCount: (state) => (slug: CartKey | null | undefined) => {
            if (!state.isHydrated || slug == null) {
                return 0
            }

            return getCartItem(state.cart, slug)?.count ?? 0
        },
    },
    actions: {
        hydrateCart(payload: unknown): void {
            this.cart = normalizeCart(payload)
            this.isHydrated = true
        },
        addToCart(payload: {
            slug: CartKey | null | undefined
            count?: number
        }) {
            if (payload.slug == null) {
                return
            }

            const normalizedCount = Math.max(
                1,
                Math.round(Number(payload.count ?? 1))
            )
            const existingItem = getCartItem(this.cart, payload.slug)

            if (existingItem) {
                existingItem.count += normalizedCount
                return
            }

            this.cart.push({
                slug: payload.slug,
                count: normalizedCount,
            })

            if (isCromi()) {
                useToastStore().showCartAddedToast()
            }
        },
        setCount(slug: CartKey | null | undefined, count: number): void {
            if (slug == null) {
                return
            }

            const item = getCartItem(this.cart, slug)

            if (!item) {
                return
            }

            if (count <= 0) {
                this.removeFromCart(slug)
                return
            }

            item.count = Math.round(count)
        },
        increment(slug: CartKey | null | undefined): void {
            if (slug == null) {
                return
            }

            const item = getCartItem(this.cart, slug)

            if (!item) {
                return
            }

            item.count += 1
        },
        decrement(slug: CartKey | null | undefined): void {
            if (slug == null) {
                return
            }

            const item = getCartItem(this.cart, slug)

            if (!item) {
                return
            }

            if (item.count <= 1) {
                this.removeFromCart(slug)
                return
            }

            item.count -= 1
        },
        removeFromCart(slug: CartKey | null | undefined): void {
            if (slug == null) {
                return
            }

            this.cart = this.cart.filter(
                (item) => String(item.slug) !== String(slug)
            )
        },
        clearCart(): void {
            this.cart = []
        },
        enforceProductLimits(products: CartProductLimit[]): void {
            products.forEach((product) => {
                const maxCartQuantity = Number(product.max_cart_quantity)

                if (
                    product.max_cart_quantity == null ||
                    !Number.isFinite(maxCartQuantity)
                ) {
                    return
                }

                const item = getCartItem(this.cart, product.slug)

                if (!item || item.count <= maxCartQuantity) {
                    return
                }

                if (maxCartQuantity <= 0) {
                    this.removeFromCart(product.slug)
                    return
                }

                item.count = Math.round(maxCartQuantity)
            })
        },
    },
})
